import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useInquiry } from '../../../hooks/useInquiry';
import { useAuth } from '../../../hooks/useAuth';
import ClientLayout from '../../../components/layout/ClientLayout';
import { InquiryStatus, InquiryCategory } from '../../../types/inquiry';

const InquiryListPage: React.FC = () => {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { inquiries, loading, error, statistics, hasUnreadMessages } = useInquiry({
    realtime: true
  });

  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<InquiryCategory | 'all'>('all');
  const [newInquiryId, setNewInquiryId] = useState<string | null>(null);

  // 실시간 업데이트 상태
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const [newResponseCount, setNewResponseCount] = useState(0);
  const [prevInquiriesCount, setPrevInquiriesCount] = useState(0);

  // 새 문의 생성 성공 시 처리
  useEffect(() => {
    if (location.state?.newInquiryId) {
      setNewInquiryId(location.state.newInquiryId);
      // 5초 후 하이라이트 제거
      const timer = setTimeout(() => setNewInquiryId(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // 실시간 업데이트 감지
  useEffect(() => {
    if (!loading && inquiries.length > 0) {
      setConnectionStatus('connected');
      setLastUpdateTime(new Date());

      // 새 답변이나 상태 변경 감지
      if (prevInquiriesCount > 0) {
        const newResponsesCount = inquiries.filter(inquiry => {
          return inquiry.adminResponse || (inquiry.messages && inquiry.messages.length > 0);
        }).length;

        const prevResponsesCount = prevInquiriesCount > 0 ?
          Math.floor(prevInquiriesCount * 0.3) : 0; // 대략적인 계산

        if (newResponsesCount > prevResponsesCount) {
          setNewResponseCount(prev => prev + (newResponsesCount - prevResponsesCount));
        }
      }

      setPrevInquiriesCount(inquiries.length);
    } else if (loading) {
      setConnectionStatus('connecting');
    }
  }, [inquiries, loading, prevInquiriesCount]);

  // 연결 상태 모니터링
  useEffect(() => {
    if (error) {
      setConnectionStatus('disconnected');
    }
  }, [error]);

  // 새 응답 알림 클리어
  const clearNewResponseNotification = useCallback(() => {
    setNewResponseCount(0);
  }, []);

  // 필터링된 문의들
  const displayInquiries = useMemo(() => {
    let result = inquiries;

    if (statusFilter !== 'all') {
      result = result.filter(inquiry => inquiry.status === statusFilter);
    }

    if (categoryFilter !== 'all') {
      result = result.filter(inquiry => inquiry.category === categoryFilter);
    }

    return result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [inquiries, statusFilter, categoryFilter]);

  // 상태별 개수 계산
  const statusCounts = useMemo(() => {
    return {
      all: displayInquiries.length,
      pending: displayInquiries.filter(i => i.status === 'pending').length,
      answered: displayInquiries.filter(i => i.status === 'answered').length,
      closed: displayInquiries.filter(i => i.status === 'closed').length
    };
  }, [displayInquiries]);

  // 상태 라벨 변환
  const getStatusLabel = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return '답변대기';
      case 'answered': return '답변완료';
      case 'closed': return '처리완료';
      default: return status;
    }
  };

  // 카테고리 라벨 변환
  const getCategoryLabel = (category: InquiryCategory): string => {
    switch (category) {
      case 'general': return '일반문의';
      case 'portville': return '포트빌';
      case 'parking': return '주차장';
      case 'facility': return '시설물';
      case 'account': return '계정';
      default: return category;
    }
  };

  // 우선순위는 관리자만 보는 정보이므로 사용자 UI에서 제거

  // 상태 색상
  const getStatusColor = (status: InquiryStatus): string => {
    switch (status) {
      case 'pending': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'answered': return 'text-green-600 bg-green-50 border-green-200';
      case 'closed': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // 날짜 포맷팅
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // 인증 상태 로딩 중
  if (authLoading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">로그인 상태를 확인하는 중...</span>
          </div>
        </div>
      </ClientLayout>
    );
  }

  // 인증되지 않은 사용자
  if (!user) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-gray-600 text-lg mb-4">로그인이 필요합니다</div>
            <div className="text-gray-500 mb-6">고객센터를 이용하려면 로그인해주세요.</div>
            <Link
              to="/signin"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              로그인하기
            </Link>
          </div>
        </div>
      </ClientLayout>
    );
  }

  // 에러 발생
  if (error) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-red-600 text-lg mb-4">오류가 발생했습니다</div>
            <div className="text-gray-600">{error}</div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-[#1e2c49] mr-4">고객센터</h1>

              {/* 실시간 연결 상태 */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${
                    connectionStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                    connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                    'bg-red-500'
                  }`}></div>
                  <span className={`text-xs font-medium ${
                    connectionStatus === 'connected' ? 'text-green-700' :
                    connectionStatus === 'connecting' ? 'text-yellow-700' :
                    'text-red-700'
                  }`}>
                    {connectionStatus === 'connected' ? '실시간 연결' :
                     connectionStatus === 'connecting' ? '연결 중' :
                     '연결 끊어짐'}
                  </span>
                </div>

                {/* 새 응답 알림 */}
                {newResponseCount > 0 && (
                  <div className="flex items-center">
                    <div className="bg-green-100 border border-green-200 rounded-lg px-2 py-1 flex items-center">
                      <svg className="w-3 h-3 text-green-600 mr-1 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-800 font-medium text-xs">새 답변 {newResponseCount}건</span>
                      <button
                        onClick={clearNewResponseNotification}
                        className="ml-1 text-green-600 hover:text-green-800"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* 마지막 업데이트 시간 */}
                <div className="text-xs text-gray-500">
                  업데이트: {lastUpdateTime.toLocaleTimeString()}
                </div>
              </div>
            </div>
            <Link
              to="/inquiry/create"
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              문의하기
            </Link>
          </div>

          {/* 통계 */}
          {statistics && (
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-gray-900">{statistics.total}</div>
                <div className="text-sm text-gray-600">전체 문의</div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-orange-600">{statistics.pending}</div>
                <div className="text-sm text-gray-600">답변대기</div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-green-600">{statistics.answered}</div>
                <div className="text-sm text-gray-600">답변완료</div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-gray-600">{statistics.closed}</div>
                <div className="text-sm text-gray-600">처리완료</div>
              </div>
            </div>
          )}
        </div>

        {/* 상태 필터 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* 카테고리 필터 */}
              <div className="flex gap-4">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value as InquiryCategory | 'all')}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">모든 카테고리</option>
                  <option value="general">일반문의</option>
                  <option value="portville">포트빌</option>
                  <option value="parking">주차장</option>
                  <option value="facility">시설물</option>
                  <option value="account">계정</option>
                </select>
              </div>
            </div>

            {/* 상태 탭 */}
            <div className="flex gap-2 mt-4">
              {(['all', 'pending', 'answered', 'closed'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    statusFilter === status
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {status === 'all' ? '전체' : getStatusLabel(status as InquiryStatus)}
                  <span className="ml-2 text-xs">
                    ({status === 'all' ? statusCounts.all : statusCounts[status as keyof typeof statusCounts]})
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 문의 목록 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-xl font-bold text-[#1e2c49] mb-4">내 문의 목록</h2>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600">문의 목록을 불러오는 중...</span>
                </div>
              </div>
            ) : displayInquiries.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">문의가 없습니다</h3>
                <p className="text-gray-600 mb-6">
                  {statusFilter !== 'all' || categoryFilter !== 'all'
                    ? '선택한 조건에 맞는 문의가 없습니다.'
                    : '아직 작성한 문의가 없습니다.'}
                </p>
                <Link
                  to="/inquiry/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  첫 문의 작성하기
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {displayInquiries.map((inquiry) => {
                  const hasUnread = user ? hasUnreadMessages(inquiry, user.uid) : false;

                  // 최근 답변이 달렸는지 확인 (1시간 이내)
                  const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
                  const hasRecentResponse = inquiry.adminResponse &&
                    inquiry.adminResponse.respondedAt > hourAgo;
                  const hasRecentMessage = inquiry.messages?.some(msg =>
                    msg.senderType === 'admin' && msg.createdAt > hourAgo
                  );
                  const isRecentlyUpdated = hasRecentResponse || hasRecentMessage;

                  return (
                    <Link
                      key={inquiry.id}
                      to={`/inquiry/${inquiry.id}`}
                      className={`block border rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 ${
                        newInquiryId === inquiry.id
                          ? 'border-blue-300 bg-blue-50 shadow-md'
                          : hasUnread
                          ? 'border-orange-300 bg-orange-50 shadow-sm'
                          : isRecentlyUpdated
                          ? 'border-green-300 bg-green-50 shadow-sm animate-fadeIn'
                          : 'border-gray-200'
                      }`}
                    >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
                            {inquiry.subject}
                          </h3>
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {inquiry.content}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-2 ml-4">
                          <div className="flex items-center gap-2">
                            {isRecentlyUpdated && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full border border-green-200 animate-pulse">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                NEW
                              </span>
                            )}
                            {hasUnread && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-800 bg-orange-100 rounded-full border border-orange-200">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                새 답변
                              </span>
                            )}
                            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(inquiry.status)}`}>
                              {getStatusLabel(inquiry.status)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <span className="inline-flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            {getCategoryLabel(inquiry.category)}
                          </span>
                          {inquiry.attachments && inquiry.attachments.length > 0 && (
                            <span className="inline-flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              첨부 {inquiry.attachments.length}개
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span>{formatDate(inquiry.createdAt)}</span>
                          {inquiry.adminResponse && (
                            <span className="text-green-600 font-medium">관리자 답변완료</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default InquiryListPage;